import { FilterFilled, EyeOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { ProductionAnalysisItem, MaterialItem } from "shared/api";
import { dayjs } from "shared/helpers";
import {
  DatePickerValue,
  DateRange,
  TableSearchFilter,
  TableRadioGroup,
} from "shared/ui";
import { AdaptedValues } from "shared/types";
import { useState } from "react";

interface UseProductionAnalysisTableColumnsReturn {
  columns: ColumnsType<ProductionAnalysisItem>;
  showMaterialModal: boolean;
  selectedMaterialItems: MaterialItem[];
  selectedOrderCode: string;
  handleCloseMaterialModal: () => void;
  showSpeedAnalysisModal: boolean;
  selectedSpeedAnalysisId: number | null;
  handleCloseSpeedAnalysisModal: () => void;
}

interface UseProductionAnalysisTableColumnsProps {
  orderCodeValue: string;
  handleOrderCodeValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearOrderCodeValue: () => void;
  handleDateGreater: (date: DatePickerValue, dateString: string) => void;
  handleDateLesser: (date: DatePickerValue, dateString: string) => void;
  isDateFilterActive: boolean;
  machineFilter: number | string;
  handleMachineFilter: (value: number | string) => void;
  adaptedMachines: AdaptedValues[];
  userFilter: number | string;
  handleUserFilter: (value: number | string) => void;
  adaptedUsers: AdaptedValues[];
  speedMinValue: string;
  handleSpeedMinValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSpeedMinValue: () => void;
  speedMaxValue: string;
  handleSpeedMaxValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSpeedMaxValue: () => void;
  processStepValue: string;
  handleProcessStepValue: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearProcessStepValue: () => void;
  deviationMinValue: string;
  handleDeviationMinValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearDeviationMinValue: () => void;
  deviationMaxValue: string;
  handleDeviationMaxValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearDeviationMaxValue: () => void;
}

export const useProductionAnalysisTableColumns = ({
  orderCodeValue,
  handleOrderCodeValue,
  clearOrderCodeValue,
  handleDateGreater,
  handleDateLesser,
  isDateFilterActive,
  machineFilter,
  handleMachineFilter,
  adaptedMachines,
  userFilter,
  handleUserFilter,
  adaptedUsers,
  speedMinValue,
  handleSpeedMinValue,
  clearSpeedMinValue,
  speedMaxValue,
  handleSpeedMaxValue,
  clearSpeedMaxValue,
  clearProcessStepValue,
  handleProcessStepValue,
  processStepValue,
  deviationMinValue,
  handleDeviationMinValue,
  clearDeviationMinValue,
  deviationMaxValue,
  handleDeviationMaxValue,
  clearDeviationMaxValue,
}: UseProductionAnalysisTableColumnsProps): UseProductionAnalysisTableColumnsReturn => {
  const timezone = localStorage.getItem("timezone") || "UTC";
  const [selectedMaterialItems, setSelectedMaterialItems] = useState<
    MaterialItem[]
  >([]);
  const [selectedOrderCode, setSelectedOrderCode] = useState<string>("");
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showSpeedAnalysisModal, setShowSpeedAnalysisModal] = useState(false);
  const [selectedSpeedAnalysisId, setSelectedSpeedAnalysisId] = useState<
    number | null
  >(null);

  const handleMaterialItemsClick = (
    materialItems: MaterialItem[],
    orderCode: string
  ) => {
    setSelectedMaterialItems(materialItems);
    setSelectedOrderCode(orderCode);
    setShowMaterialModal(true);
  };

  const handleCloseMaterialModal = () => {
    setShowMaterialModal(false);
    setSelectedMaterialItems([]);
    setSelectedOrderCode("");
  };

  const handleSpeedAnalysisClick = (id: number) => {
    setSelectedSpeedAnalysisId(id);
    setShowSpeedAnalysisModal(true);
  };

  const handleCloseSpeedAnalysisModal = () => {
    setShowSpeedAnalysisModal(false);
    setSelectedSpeedAnalysisId(null);
  };

  const getPercentagesColor = (v: number) => {
    if (v <= -10) return "#e00016";
    if (v <= -5) return "#f6ff00";

    return "#00ff08";
  };

  return {
    columns: [
      {
        title: "Order",
        render: (record: ProductionAnalysisItem) => {
          const orderCode =
            record?.order_process_step_tracker?.order?.order_code;
          if (!orderCode) return "N/A";

          return (
            <Link
              to={`/process-step-production/${orderCode}/index-0`}
              className="text-blue-600 underline"
            >
              {orderCode}
            </Link>
          );
        },
        filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
          <TableSearchFilter
            setSelectedKeys={setSelectedKeys}
            clearFilters={clearOrderCodeValue}
            confirm={confirm}
            value={orderCodeValue}
            handleFilterChange={handleOrderCodeValue}
            visible={visible}
            placeholder="Filter by Order Code"
            title="Order Code"
          />
        ),
        filterIcon: (
          <FilterFilled
            className={orderCodeValue.length ? "!text-blue-600" : ""}
          />
        ),
      },
      {
        title: "Process Step",
        render: (record: ProductionAnalysisItem) => {
          const processStep = record?.order_process_step_tracker?.process_step;
          const processStepName = processStep?.name;
          const processStepId = processStep?.id;

          if (!processStepName) return "N/A";

          return (
            <Link
              to={`/process-steps/hyperlink/${processStepId}`}
              className="text-blue-600 underline"
            >
              {processStepName}
            </Link>
          );
        },
        filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
          <TableSearchFilter
            setSelectedKeys={setSelectedKeys}
            clearFilters={clearProcessStepValue}
            confirm={confirm}
            value={processStepValue}
            handleFilterChange={handleProcessStepValue}
            visible={visible}
            placeholder="Filter by Process Step"
            title="Process Step"
          />
        ),
        filterIcon: (
          <FilterFilled
            className={processStepValue.length ? "!text-blue-600" : ""}
          />
        ),
      },
      {
        title: "Machine",
        render: (record: ProductionAnalysisItem) => {
          const machine = record?.machine;
          const machineName = machine?.name;
          const machineId = machine?.id;

          if (!machineName) return "N/A";

          return (
            <Link
              to={`/machines/hyperlink/${machineId}`}
              className="text-blue-600 underline"
            >
              {machineName}
            </Link>
          );
        },
        filterDropdown: () => (
          <TableRadioGroup
            options={adaptedMachines}
            selectedValue={machineFilter}
            onChange={handleMachineFilter}
          />
        ),
        filterIcon: (
          <FilterFilled className={machineFilter ? "!text-blue-600" : ""} />
        ),
      },
      {
        title: "User",
        render: (record: ProductionAnalysisItem) =>
          record?.user
            ? `${record.user.first_name} ${record.user.last_name}`
            : "N/A",
        filterDropdown: () => (
          <TableRadioGroup
            options={adaptedUsers}
            selectedValue={userFilter}
            onChange={handleUserFilter}
          />
        ),
        filterIcon: (
          <FilterFilled className={userFilter ? "!text-blue-600" : ""} />
        ),
      },
      {
        title: "Articles",
        render: (record: ProductionAnalysisItem) => {
          const materialItems = record?.materialitem_set || [];
          if (materialItems.length === 0) return "N/A";

          const firstItem = materialItems[0];
          const serialNumber = firstItem?.serial_number || "N/A";
          const totalCount = materialItems.length;
          const orderCode =
            record?.order_process_step_tracker?.order?.order_code || "";

          return (
            <button
              className="text-blue-600 underline cursor-pointer bg-transparent border-none p-0"
              onClick={() => handleMaterialItemsClick(materialItems, orderCode)}
              type="button"
            >
              {`${serialNumber} (${totalCount})`}
            </button>
          );
        },
      },
      {
        title: "Timestamp",
        render: (record: ProductionAnalysisItem) =>
          record?.timestamp
            ? dayjs(record.timestamp).tz(timezone).format("YYYY-MM-DD HH:mm")
            : "N/A",
        filterIcon: (
          <FilterFilled className={isDateFilterActive ? "!text-red-600" : ""} />
        ),
        filterDropdown: () => {
          return (
            <DateRange
              title="Timestamp"
              leftPickerTitle="From"
              rightPickerTitle="To"
              handlePickerChangeGreater={handleDateGreater}
              handlePickerChangeLesser={handleDateLesser}
            />
          );
        },
      },
      {
        title: "Speed",
        render: (record: ProductionAnalysisItem) => {
          const speedValue =
            record?.detected_speed !== null
              ? Math.floor(record.detected_speed)
              : "N/A";

          return (
            <div className="flex items-center gap-2">
              <span>{speedValue}</span>
              {record?.detected_speed !== null && (
                <button
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  onClick={() => handleSpeedAnalysisClick(record.id)}
                  title="View speed analysis"
                  type="button"
                >
                  <EyeOutlined className="w-5 h-5" />
                </button>
              )}
            </div>
          );
        },
        filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
          <div className="flex-col p-3">
            <div className="mb-2">
              <TableSearchFilter
                setSelectedKeys={setSelectedKeys}
                clearFilters={clearSpeedMinValue}
                confirm={confirm}
                value={speedMinValue}
                handleFilterChange={handleSpeedMinValue}
                visible={visible}
                placeholder="Min Speed"
                title="Min Speed"
              />
            </div>
            <TableSearchFilter
              setSelectedKeys={setSelectedKeys}
              clearFilters={clearSpeedMaxValue}
              confirm={confirm}
              value={speedMaxValue}
              handleFilterChange={handleSpeedMaxValue}
              visible={visible}
              placeholder="Max Speed"
              title="Max Speed"
            />
          </div>
        ),
        filterIcon: (
          <FilterFilled
            className={
              speedMinValue.length || speedMaxValue.length
                ? "!text-blue-600"
                : ""
            }
          />
        ),
      },
      {
        title: "Target Speed",
        render: (record: ProductionAnalysisItem) => {
          const processStep = record?.order_process_step_tracker?.process_step;
          const processStepId = processStep?.id;
          const targetSpeed = record?.detected_target_speed;

          if (targetSpeed === null) return "N/A";

          return (
            <Link
              to={`/process-steps/hyperlink/${processStepId}`}
              className="text-blue-600 underline"
            >
              {Math.floor(targetSpeed)}
            </Link>
          );
        },
      },
      {
        title: "Deviation %",
        render: (record: ProductionAnalysisItem) => {
          const deviationPercentages = record?.calc_deviation_percentage;

          if (deviationPercentages === null) return "N/A";

          return (
            <span
              style={{
                background: getPercentagesColor(deviationPercentages),
                color: deviationPercentages <= -10 ? "white" : "black",
              }}
            >
              {deviationPercentages.toFixed(1)}%
            </span>
          );
        },
        filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
          <div className="flex-col p-3">
            <div className="mb-2">
              <TableSearchFilter
                setSelectedKeys={setSelectedKeys}
                clearFilters={clearDeviationMinValue}
                confirm={confirm}
                value={deviationMinValue}
                handleFilterChange={handleDeviationMinValue}
                visible={visible}
                placeholder="Min Deviation %"
                title="Min Deviation %"
              />
            </div>
            <TableSearchFilter
              setSelectedKeys={setSelectedKeys}
              clearFilters={clearDeviationMaxValue}
              confirm={confirm}
              value={deviationMaxValue}
              handleFilterChange={handleDeviationMaxValue}
              visible={visible}
              placeholder="Max Deviation %"
              title="Max Deviation %"
            />
          </div>
        ),
        filterIcon: (
          <FilterFilled
            className={
              deviationMinValue.length || deviationMaxValue.length
                ? "!text-blue-600"
                : ""
            }
          />
        ),
      },
      {
        title: "Start",
        render: (record: ProductionAnalysisItem) =>
          record?.detected_start_timestamp
            ? dayjs(record.detected_start_timestamp)
                .tz(timezone)
                .format("YYYY-MM-DD HH:mm")
            : "N/A",
      },
      {
        title: "Stop",
        render: (record: ProductionAnalysisItem) =>
          record?.detected_stop_timestamp
            ? dayjs(record.detected_stop_timestamp)
                .tz(timezone)
                .format("YYYY-MM-DD HH:mm")
            : "N/A",
      },
    ],
    showMaterialModal,
    selectedMaterialItems,
    selectedOrderCode,
    handleCloseMaterialModal,
    showSpeedAnalysisModal,
    selectedSpeedAnalysisId,
    handleCloseSpeedAnalysisModal,
  };
};
